/**
 * @fileoverview Composable useNotification - API moderna per notifiche
 *
 * Composable moderno per gestire notifiche e messaggi con Composition API.
 * Fornisce API semplificata e type-safe mantenendo piena compatibilità.
 *
 * @author Generated by TypeScript Migration
 * @since 2025-09-25
 */

import { getCurrentInstance } from 'vue';
import type {
	NotificationService,
	NotificationType,
	MessageType,
	NotificationOptions,
	MessageOptions,
} from '@/lib/services/notification/index';

/**
 * Opzioni per il composable
 */
export interface UseNotificationOptions {
	/** Se mostrare sempre il bottone di chiusura */
	showClose?: boolean;
}

/**
 * Composable per gestione notifiche moderna
 */
export function useNotification(options: UseNotificationOptions = {}) {
	// Accesso al servizio notification tramite current instance
	const instance = getCurrentInstance();
	const $notificationService = (instance?.proxy as any)
		?.$notificationService as NotificationService;

	if (!$notificationService) {
		console.warn('⚠️ useNotification: $notificationService not available');
	}

	const { showClose = true } = options;

	/**
	 * Mostra messaggio di successo
	 */
	function success(message: string): void {
		if (!$notificationService) {
			return;
		}
		console.log('✅ SUCCESS:', message);
		$notificationService.success(message, showClose);
	}

	/**
	 * Mostra messaggio di errore
	 */
	function error(message: string, title?: string): void {
		if (!$notificationService) {
			return;
		}
		console.error('❌ ERROR:', title ? `${title}: ${message}` : message);
		$notificationService.error(message, title, showClose);
	}

	/**
	 * Mostra messaggio di warning
	 */
	function warning(message: string): void {
		if (!$notificationService) {
			return;
		}
		console.warn('⚠️ WARNING:', message);
		$notificationService.warning(message, showClose);
	}

	/**
	 * Mostra messaggio informativo
	 */
	function info(message: string): void {
		if (!$notificationService) {
			return;
		}
		console.info('ℹ️ INFO:', message);
		$notificationService.info(message, showClose);
	}

	/**
	 * Notifica personalizzata completa
	 */
	function notify(notificationOptions: NotificationOptions): void {
		if (!$notificationService) {
			return;
		}
		console.log(
			'🔔 NOTIFY:',
			notificationOptions.type?.toUpperCase() || 'CUSTOM',
			'-',
			notificationOptions.title || notificationOptions.message,
		);
		$notificationService.notify(notificationOptions);
	}

	/**
	 * Messaggio personalizzato completo
	 */
	function message(messageOptions: MessageOptions): void {
		if (!$notificationService) {
			return;
		}
		console.log(
			'💬 MESSAGE:',
			messageOptions.type?.toUpperCase() || 'DEFAULT',
			'-',
			messageOptions.message,
		);
		$notificationService.message(messageOptions);
	}

	return {
		/** Metodi semplici per notifiche comuni */
		success,
		error,
		warning,
		info,

		/** Metodi avanzati per personalizzazione completa */
		notify,
		message,

		/** Accesso diretto al servizio per retrocompatibilità */
		notificationService: $notificationService,

		/** Alias per compatibilità */
		$notificationService,
	};
}

/**
 * Re-export dei tipi per convenienza
 */
export type {
	NotificationService,
	NotificationType,
	MessageType,
	NotificationOptions,
	MessageOptions,
};
